import React from 'react';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper, { PopperPlacementType } from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { SvgIconTypeMap } from '@material-ui/core/SvgIcon/SvgIcon';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    paper: {
      marginRight: theme.spacing(2),
    },
  }),
);

export interface EeMenuProps {
  label: string;
  color?: 'primary' | 'secondary';
  variant?: 'outlined' | 'contained';
  icon?: OverridableComponent<SvgIconTypeMap>;
  items: { id: string | number; name: string }[];
  onSelect?: (item: { id: string | number; name: string }) => void;
  popperPlacement?: PopperPlacementType;
  buttonStyle?: object;
}

export const EeMenu: React.FC<Omit<EeMenuProps, 'label'> & { label: string | JSX.Element, disabled?: boolean }> = ({
  label,
  color,
  disabled,
  variant,
  icon,
  items,
  onSelect,
  popperPlacement = 'bottom-end',
}) => {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current!.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <div className={classes.root}>
      {icon ? (
        <IconButton
          ref={anchorRef}
          aria-label="more"
          aria-controls="long-menu"
          aria-haspopup="true"
          disabled={disabled}
          onClick={handleToggle}
        >
          {icon}
        </IconButton>
      ) : (
        <Button
          ref={anchorRef}
          aria-controls={open ? 'menu-list-grow' : undefined}
          aria-haspopup="true"
          color={color || 'secondary'}
          disabled={disabled}
          variant={variant || 'contained'}
          onClick={handleToggle}
        >
          {label}
        </Button>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement={popperPlacement}
        style={{ zIndex: 1 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom-end' ? 'right top' : 'right bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {items.map((item, index) =>
                    item.id ? (
                      <MenuItem
                        key={index}
                        value={item.id}
                        onClick={(e) => {
                          if (onSelect) onSelect(item);
                          handleClose(e);
                        }}
                      >
                        {item.name}
                      </MenuItem>
                    ) : (
                      <Divider key={index} className="lens-m-0" />
                    ),
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
